import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../../config";

const baseUrl = config.baseURL;

const CareerPage = () => {
  const [careerData, setCareerData] = useState([]); // Array to store all career data
  const [filteredData, setFilteredData] = useState([]); // Array to store filtered career data
  const [locations, setLocations] = useState([]); // To store unique locations for filtering
  const [departments, setDepartments] = useState([]); // To store unique departments for filtering
  const [statuses, setStatuses] = useState([]); // To store unique statuses for filtering
  const [types, setTypes] = useState([]); // To store unique types for filtering
  const [tags, setTags] = useState([]); // Added for unique tag filtering
  const [filters, setFilters] = useState({
    location: "",
    department: "",
    code: "",
    status: "",
    type: "",
    tag: "", // Added tag filter
    title: "",
  }); // Filters state
  const [userEmail, setUserEmail] = useState(""); // User email state for application

  const [error, setError] = useState(null); // Error state

  // Fetch career data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/career`);

        const data = response.data.data; // Accessing 'data' from the API response

        if (Array.isArray(data)) {
          setCareerData(data);
          setFilteredData(data);

          // Extract unique locations, departments, statuses, types, and tags for the filter options
          const uniqueLocations = [
            ...new Set(data.map((item) => item.location)),
          ];
          const uniqueDepartments = [
            ...new Set(data.map((item) => item.department)),
          ];
          const uniqueStatuses = [...new Set(data.map((item) => item.active))];
          const uniqueTypes = [...new Set(data.map((item) => item.type))];
          const uniqueTags = [...new Set(data.map((item) => item.tag))]; // Extract tags

          setLocations(uniqueLocations);
          setDepartments(uniqueDepartments);
          setStatuses(uniqueStatuses);
          setTypes(uniqueTypes);
          setTags(uniqueTags); // Set unique tags for filtering
        } else {
          setError("Career data is not an array.");
        }
      } catch (err) {
        console.error("Error fetching data:", err.message);
        setError("Failed to fetch career data");
      }
    };

    fetchData();
  }, []);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters, [name]: value };
      filterData(newFilters);
      return newFilters;
    });
  };

  // Filter the career data based on the selected filters
  const filterData = (filters) => {
    let filtered = careerData;

    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        filtered = filtered.filter((item) => {
          if (key === "title") {
            return item[key].toLowerCase().includes(filters[key].toLowerCase());
          } else if (key === "code") {
            return item[key].toLowerCase().includes(filters[key].toLowerCase());
          } else if (key === "tag") {
            return item[key] === filters[key]; // Filter by tag
          } else {
            return item[key] === filters[key];
          }
        });
      }
    });

    setFilteredData(filtered);
  };

  // Handle job application (open mailer)
  const applyForJob = (jobTitle, jobCode) => {
    // Prepare the mailto link with the job details and user's email
    const subject = `${jobCode}`;
    const body = `I am applying for the ${jobTitle} position.`;
    const mailtoLink = `mailto:?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;

    // Open the user's default mail client with pre-filled data
    window.location.href = mailtoLink;
  };

  // Handle clearing the filters
  const clearFilters = () => {
    setFilters({
      location: "",
      department: "",
      code: "",
      status: "",
      type: "",
      tag: "",
      title: "",
    });
    setFilteredData(careerData); // Reset filtered data to show all careers
  };

  // Return loading, error, or the actual career data
  if (error) {
    return <div>{error}</div>;
  }

  if (!careerData.length) {
    return <div>Loading...</div>;
  }

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#F4F6F9", // light grayish background
        minHeight: "100vh",
      }}
    >
      <h1
        style={{
          fontSize: "2.5em",
          fontWeight: "bold",
          marginBottom: "20px",
          color: "#333333", // dark gray text
        }}
      >
        Career Opportunities
      </h1>

      {/* Filters Section */}
      <div style={{ marginBottom: "20px" }}>
        <select
          name="location"
          onChange={handleFilterChange}
          value={filters.location}
          style={{
            padding: "10px",
            marginRight: "10px",
            width: "200px",
            backgroundColor: "#FFFFFF", // white background
            border: "1px solid #D1D8E0", // light gray border
            color: "#333333", // dark gray text
          }}
        >
          <option value="">Select Location</option>
          {locations.map((location, index) => (
            <option key={index} value={location}>
              {location || "Not specified"}
            </option>
          ))}
        </select>

        <select
          name="department"
          onChange={handleFilterChange}
          value={filters.department}
          style={{
            padding: "10px",
            marginRight: "10px",
            width: "200px",
            backgroundColor: "#FFFFFF",
            border: "1px solid #D1D8E0",
            color: "#333333",
          }}
        >
          <option value="">Select Department</option>
          {departments.map((department, index) => (
            <option key={index} value={department}>
              {department || "Not specified"}
            </option>
          ))}
        </select>

        <select
          name="type"
          onChange={handleFilterChange}
          value={filters.type}
          style={{
            padding: "10px",
            marginRight: "10px",
            width: "200px",
            backgroundColor: "#FFFFFF",
            border: "1px solid #D1D8E0",
            color: "#333333",
          }}
        >
          <option value="">Select Type</option>
          {types.map((type, index) => (
            <option key={index} value={type}>
              {type}
            </option>
          ))}
        </select>

        <input
          type="text"
          name="code"
          value={filters.code}
          onChange={handleFilterChange}
          placeholder="Search by Code"
          style={{
            padding: "10px",
            width: "200px",
            marginRight: "10px",
            backgroundColor: "#FFFFFF",
            border: "1px solid #D1D8E0",
            color: "#333333",
          }}
        />

        {/* Filter by Tag */}
        <select
          name="tag"
          onChange={handleFilterChange}
          value={filters.tag}
          style={{
            padding: "10px",
            marginRight: "10px",
            width: "200px",
            backgroundColor: "#FFFFFF",
            border: "1px solid #D1D8E0",
            color: "#333333",
          }}
        >
          <option value="">Select Tag</option>
          {tags.map((tag, index) => (
            <option key={index} value={tag}>
              {tag || "Not specified"}
            </option>
          ))}
        </select>

        {/* Clear Filters Button */}
        <button
          onClick={clearFilters}
          style={{
            padding: "10px",
            marginLeft: "10px",
            backgroundColor: "#2E4053", // dark blue
            color: "#FFFFFF", // white text
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          Clear Filters
        </button>
      </div>

      {/* Displaying Career Data in Cards */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
        {filteredData.map((career) => (
          <div
            key={career.id}
            style={{
              backgroundColor: "#FFFFFF", // white background
              border: "1px solid #D1D8E0", // light gray border
              borderRadius: "8px",
              padding: "20px",
              width: "300px",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
              transition: "transform 0.3s ease-in-out",
              cursor: "pointer",
            }}
          >
            <h3>{career.title}</h3>
            <p>
              <strong>Department:</strong> {career.department}
            </p>
            <p>
              <strong>Location:</strong> {career.location || "Not specified"}
            </p>
            <p>
              <strong>Type:</strong> {career.type}
            </p>
            <p>
              <strong>Status:</strong> {career.active}
            </p>
            <p>
              <strong>Tag:</strong> {career.tag || "Not specified"}
            </p>
            <p>
              <strong>Description:</strong> {career.description}
            </p>
            <p>
              <strong>Code:</strong> {career.code}
            </p>
            <button
              onClick={() => applyForJob(career.title, career.code)}
              style={{
                backgroundColor: "#2E4053", // dark blue
                color: "#FFFFFF", // white text
                padding: "10px",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                marginTop: "10px",
              }}
            >
              Apply
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CareerPage;
